import { useState, useEffect, useCallback } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { ServiceBusTopic, ServiceBusSubscription } from '../types';

interface UseServiceBusConfigResult {
  topics: ServiceBusTopic[];
  subscriptions: ServiceBusSubscription[];
  loading: boolean;
  error: Error | undefined;
  reload: () => void;
}

/**
 * Parse HCL object fields from content inside { ... }
 */
function parseHclFields(objectContent: string): Record<string, string> {
  const fields: Record<string, string> = {};
  const fieldPattern = /^\s*(\w+)\s*=\s*(.+?)\s*$/gm;

  let matchResult = fieldPattern.exec(objectContent);
  while (matchResult !== null) {
    const key = matchResult[1];
    let value = matchResult[2].trim();

    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    fields[key] = value;

    matchResult = fieldPattern.exec(objectContent);
  }

  return fields;
}

/**
 * Parse HCL objects from array content
 */
function parseHclObjects(content: string): Record<string, string>[] {
  const items: Record<string, string>[] = [];
  let pos = 0;

  while (pos < content.length) {
    const braceStart = content.indexOf('{', pos);
    if (braceStart === -1) break;

    let depth = 1;
    let braceEnd = braceStart + 1;
    let inStr = false;
    let strChr = '';

    while (braceEnd < content.length && depth > 0) {
      const ch = content[braceEnd];
      const prev = braceEnd > 0 ? content[braceEnd - 1] : '';

      if ((ch === '"' || ch === "'") && prev !== '\\') {
        if (!inStr) {
          inStr = true;
          strChr = ch;
        } else if (ch === strChr) {
          inStr = false;
        }
      }

      if (!inStr) {
        if (ch === '{') depth++;
        else if (ch === '}') depth--;
      }

      braceEnd++;
    }

    const objectContent = content.substring(braceStart + 1, braceEnd - 1);
    items.push(parseHclFields(objectContent));
    pos = braceEnd;
  }

  return items;
}

/**
 * Find attribute array block in HCL content and return its inner content
 */
function findArrayContent(
  content: string,
  attributeName: string,
): string | null {
  const pattern = new RegExp(`^\\s*${attributeName}\\s*=\\s*\\[`, 'gm');

  const match = pattern.exec(content);
  if (!match) return null;

  const startIndex = match.index + match[0].length;
  let bracketCount = 1;
  let currentIndex = startIndex;
  let inString = false;
  let stringChar = '';

  while (currentIndex < content.length && bracketCount > 0) {
    const char = content[currentIndex];
    const prevChar = currentIndex > 0 ? content[currentIndex - 1] : '';

    if ((char === '"' || char === "'") && prevChar !== '\\') {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
      }
    }

    if (!inString) {
      if (char === '[' || char === '{') bracketCount++;
      else if (char === ']' || char === '}') bracketCount--;
    }

    currentIndex++;
  }

  if (bracketCount !== 0) return null;

  return content.substring(startIndex, currentIndex - 1);
}

/**
 * Parse topics from raw HCL content
 */
function parseTopicsFromHcl(content: string): ServiceBusTopic[] {
  const arrayContent = findArrayContent(content, 'asb_topics_name');
  if (!arrayContent) return [];

  return parseHclObjects(arrayContent)
    .filter(obj => obj.name)
    .map(obj => ({ name: obj.name }));
}

/**
 * Parse subscriptions from raw HCL content
 */
function parseSubscriptionsFromHcl(content: string): ServiceBusSubscription[] {
  const arrayContent = findArrayContent(content, 'asb_subs');
  if (!arrayContent) return [];

  return parseHclObjects(arrayContent)
    .filter(obj => obj.name && obj.topic)
    .map(obj => {
      const sub: ServiceBusSubscription = {
        name: obj.name,
        topic: obj.topic,
        sql_filter: obj.sql_filter || '',
      };

      if (obj.lock_duration) {
        sub.lock_duration = obj.lock_duration;
      }

      if (obj.max_delivery_count) {
        const parsed = parseInt(obj.max_delivery_count, 10);
        if (!isNaN(parsed)) {
          sub.max_delivery_count = parsed;
        }
      }

      return sub;
    });
}

/**
 * Hook to fetch and parse Service Bus configuration from GitLab
 * via the Backstage proxy endpoint.
 */
export const useServiceBusConfig = (
  projectId: number,
  environment: string,
): UseServiceBusConfigResult => {
  const configApi = useApi(configApiRef);
  const [topics, setTopics] = useState<ServiceBusTopic[]>([]);
  const [subscriptions, setSubscriptions] = useState<ServiceBusSubscription[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const reload = useCallback(() => {
    setReloadTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!environment || !projectId) {
      setTopics([]);
      setSubscriptions([]);
      return;
    }

    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const baseUrl = configApi.getString('backend.baseUrl');
        const filePath = `terraform/live/shared/${environment}/azure-service-bus/main.tf`;
        const encodedPath = encodeURIComponent(filePath);

        const url = `${baseUrl}/api/proxy/gitlab/api/projects/${projectId}/repository/files/${encodedPath}/raw?ref=master`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(
            `Falha ao buscar configuração do Service Bus: ${response.status} ${response.statusText}`,
          );
        }

        const content = await response.text();

        const parsedTopics = parseTopicsFromHcl(content);
        const parsedSubs = parseSubscriptionsFromHcl(content);

        setTopics(parsedTopics);
        setSubscriptions(parsedSubs);
      } catch (err) {
        setError(err as Error);
        setTopics([]);
        setSubscriptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [configApi, projectId, environment, reloadTrigger]);

  return {
    topics,
    subscriptions,
    loading,
    error,
    reload,
  };
};
